import { emitError } from '@features/observability/lib/errorBus'
import type { ToastAction } from '@lib/emitToast'
import { getResourceLabel, getResourceUrl } from '@lib/resourceUrl'
import { splitAndCapitalFirstLetter } from '@lib/textFormatters'

type FormattedError = { message: string; details: { message: string }[]; actions?: ToastAction[] }

type DetailData = { type: 'link'; resource_type?: string; resource_id?: string | number }

type ErrorOldShape = {
  message?: string
  details?: Array<{
    message: string
    data?: unknown
  }>
}
type ErrorNewShape = {
  // "New" error shape
  message?: string
  data?: {
    errors?: Record<string, Array<string>>
  }
  errors?: Record<string, Array<string>>
}
type ErrorData = {
  error?: ErrorOldShape // "Old" error shape
} & ErrorNewShape // "New" error shape

const linkAction = ({ resource_type, resource_id }: DetailData): ToastAction[] => {
  if (typeof resource_type !== 'string' || resource_id == null) return []

  const href = getResourceUrl(resource_type, resource_id)

  // Deliberate 4xx report: a routable descriptor we cannot map means RESOURCE_URL_MAP is missing an entry.
  if (!href) {
    emitError({
      message: `Unmapped toast link resource: ${resource_type}`,
      source: 'api',
      fingerprint: `toast-link:unmapped:${resource_type}`,
    })

    return []
  }

  return [{ label: getResourceLabel(resource_type, resource_id), href }]
}

const toastActions = (details: ErrorOldShape['details'] = []): ToastAction[] =>
  details.flatMap(({ data }) => {
    const detail = data as DetailData | undefined

    switch (detail?.type) {
      case 'link':
        return linkAction(detail)
      default:
        return []
    }
  })

const errorShapeOld = (error: ErrorOldShape): FormattedError => {
  const actions = toastActions(error?.details)

  return {
    message: error?.message || 'Error',
    details: error?.details ?? [],
    ...(actions.length > 0 && { actions }),
  }
}

const errorShapeNew = (error: ErrorNewShape): FormattedError => {
  const errors = error?.data?.errors || error?.errors

  const result: FormattedError = { message: error?.message || 'Error', details: [] }

  if (errors && typeof errors === 'object') {
    const formattedErrors = Object.entries(errors).map(([field, messages]) => {
      const errorName = splitAndCapitalFirstLetter(field)
      const getErrorMessage = (errors: string | string[]) => {
        if (Array.isArray(errors)) {
          return errors.join(', ')
        } else {
          return typeof errors === 'object'
            ? Object.entries(errors)
                .map(([title, message]) => `${title} ${message}`)
                .join(', ')
            : errors
        }
      }

      const errorMessages = getErrorMessage(messages)

      return { message: `${errorName}: ${splitAndCapitalFirstLetter(errorMessages)}` }
    })

    if (formattedErrors.length > 0) {
      result.details = formattedErrors
    }
  }

  return result
}

export const formatErrorMessage = (errorData: ErrorData): FormattedError => {
  try {
    // "Old" error shape
    if (errorData?.error) {
      return errorShapeOld(errorData?.error)
    }

    // "New" error shape
    return errorShapeNew(errorData)
  } catch {
    return { message: 'Error', details: [] }
  }
}
